/**
 * Reusable HTML snippets for the CMS visual editor (TinyMCE templates + sidebar).
 * Inline styles keep previews consistent inside the editor iframe.
 */

export interface CMSEditorBlock {
	id: string;
	title: string;
	description: string;
	/** HTML inserted at cursor */
	html: string;
}

export type CMSPageLayoutTemplate =
	| "home"
	| "landing"
	| "legal"
	| "generic"
	| "crisis"
	| "program-overview"
	| "story-community"
	| "trust-legal"
	| "partnership-cta"
	| "about-mission"
	| "video-gallery"
	| "photo-gallery"
	| "patient-stories";

export interface CMSPageTemplateOption {
	id: CMSPageLayoutTemplate;
	label: string;
	description: string;
	defaultType?: "home" | "landing" | "legal" | "generic";
	defaultShowTitle?: boolean;
	html: string;
}

/** Set on drag from the blocks palette so the editor can insert HTML at the drop coordinates. */
export const CMS_BLOCK_DRAG_MIME = "application/x-gsgo-cms-block";

const BLANK_SECTION = `
<section class="cms-block-blank-section" style="margin:0;padding:56px 20px;background:#ffffff;border-top:1px dashed #cbd5e1;border-bottom:1px dashed #cbd5e1;">
  <div style="max-width:1100px;margin:0 auto;">
    <h2 style="margin:0;font-size:30px;line-height:1.2;font-weight:900;color:#1A5276;">New section title</h2>
    <p style="margin:14px 0 0 0;font-size:16px;line-height:1.7;color:#475569;">Start writing your section content here. Replace this text with your actual copy.</p>
  </div>
</section>
`.trim();

/** Three-column feature cards (matches common landing sections). */
const THREE_COLUMN_CARDS = `
<section class="cms-block-cards" style="margin:0;padding:48px 20px;background:#ffffff;">
  <div style="max-width:1100px;margin:0 auto;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:20px;">
    <article style="border:1px solid #e5e7eb;border-radius:12px;padding:24px;background:#fff;">
      <h3 style="margin:0;font-size:20px;font-weight:800;color:#1A5276;">Clear Need</h3>
      <p style="margin:10px 0 0 0;font-size:14px;font-weight:700;color:#1C2833;">Problem-focused support</p>
      <p style="margin:12px 0 0 0;font-size:14px;line-height:1.65;color:#64748b;">Medication Aid addresses real, urgent barriers that affect treatment continuity and family stability.</p>
    </article>
    <article style="border:1px solid #e5e7eb;border-radius:12px;padding:24px;background:#fff;">
      <h3 style="margin:0;font-size:20px;font-weight:800;color:#148F77;">Practical Action</h3>
      <p style="margin:10px 0 0 0;font-size:14px;font-weight:700;color:#1C2833;">Execution with accountability</p>
      <p style="margin:12px 0 0 0;font-size:14px;line-height:1.65;color:#64748b;">Support pathways are designed for measurable outcomes and transparent implementation.</p>
    </article>
    <article style="border:1px solid #e5e7eb;border-radius:12px;padding:24px;background:#fff;">
      <h3 style="margin:0;font-size:20px;font-weight:800;color:#C0392B;">Community Impact</h3>
      <p style="margin:10px 0 0 0;font-size:14px;font-weight:700;color:#1C2833;">Long-term resilience</p>
      <p style="margin:12px 0 0 0;font-size:14px;line-height:1.65;color:#64748b;">Programs and partnerships are built to sustain care beyond one-time interventions.</p>
    </article>
  </div>
</section>
`.trim();

const TWO_COLUMN_SPLIT = `
<section class="cms-block-split" style="margin:0;padding:48px 20px;background:#F8FAFC;">
  <div style="max-width:1100px;margin:0 auto;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:28px;align-items:start;">
    <div>
      <p style="margin:0;font-size:12px;letter-spacing:0.25em;text-transform:uppercase;font-weight:800;color:#148F77;">Section label</p>
      <h2 style="margin:12px 0 0 0;font-size:32px;line-height:1.2;font-weight:900;color:#1A5276;">Headline for this column</h2>
      <p style="margin:14px 0 0 0;font-size:16px;line-height:1.7;color:#475569;">Supporting paragraph text. Edit this copy to match your page.</p>
    </div>
    <div style="border-radius:12px;border:1px solid #e2e8f0;background:#fff;padding:22px;">
      <p style="margin:0;font-size:15px;line-height:1.7;color:#334155;">Secondary column for highlights, bullets, or a short list.</p>
      <ul style="margin:14px 0 0 0;padding-left:1.25rem;color:#334155;font-size:15px;line-height:1.6;">
        <li>First point</li>
        <li>Second point</li>
        <li>Third point</li>
      </ul>
    </div>
  </div>
</section>
`.trim();

const CTA_BAND = `
<section class="cms-block-cta" style="margin:0;padding:40px 20px;background:linear-gradient(90deg,#1A5276 0%,#148F77 100%);color:#fff;text-align:center;">
  <div style="max-width:720px;margin:0 auto;">
    <h2 style="margin:0;font-size:28px;font-weight:900;">Ready to take the next step?</h2>
    <p style="margin:14px 0 0 0;font-size:16px;line-height:1.6;opacity:0.95;">Short supporting sentence. Link your primary action below.</p>
    <p style="margin:22px 0 0 0;">
      <a href="/contact-us" style="display:inline-block;background:#D4AC0D;color:#1C2833;font-weight:800;padding:12px 24px;border-radius:999px;text-decoration:none;">Contact us</a>
    </p>
  </div>
</section>
`.trim();

const QUOTE_BLOCK = `
<blockquote class="cms-block-quote" style="margin:24px auto;max-width:720px;padding:24px 28px;border-left:4px solid #1A5276;background:#F1F5F9;font-size:18px;line-height:1.65;color:#1e293b;font-style:italic;">
  “Replace this quote with a testimonial or key message from your community.”
  <footer style="margin-top:14px;font-size:14px;font-style:normal;font-weight:700;color:#1A5276;">— Attribution name, role</footer>
</blockquote>
`.trim();

const HOME_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#f8fafc;color:#1C2833;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="background:linear-gradient(180deg,#1A5276 0%,#0D3349 100%);color:#fff;">
    <div style="max-width:1100px;margin:0 auto;padding:56px 20px 64px;">
      <p style="margin:0;font-size:12px;letter-spacing:0.35em;text-transform:uppercase;font-weight:800;color:#D4AC0D;">Home</p>
      <h1 style="margin:16px 0 0 0;max-width:900px;font-size:46px;line-height:1.12;font-weight:900;">Homepage headline that communicates your mission clearly.</h1>
      <p style="margin:18px 0 0 0;max-width:860px;font-size:20px;line-height:1.7;color:rgba(255,255,255,0.92);">Lead with your strongest mission statement and one clear action you want visitors to take.</p>
    </div>
  </section>
  <section style="max-width:1100px;margin:-28px auto 0;padding:0 20px 12px;">
    <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px;">
      <article style="border:1px solid #dbeafe;background:#fff;border-radius:14px;padding:20px;"><p style="margin:0;font-size:13px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#2563eb;">Impact</p><p style="margin:8px 0 0 0;font-size:32px;font-weight:900;color:#1e3a8a;">2,347</p><p style="margin:8px 0 0 0;font-size:14px;line-height:1.6;color:#475569;">Replace with your top metric.</p></article>
      <article style="border:1px solid #d1fae5;background:#fff;border-radius:14px;padding:20px;"><p style="margin:0;font-size:13px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#059669;">Reach</p><p style="margin:8px 0 0 0;font-size:32px;font-weight:900;color:#065f46;">14 Countries</p><p style="margin:8px 0 0 0;font-size:14px;line-height:1.6;color:#475569;">Replace with your coverage stat.</p></article>
      <article style="border:1px solid #fee2e2;background:#fff;border-radius:14px;padding:20px;"><p style="margin:0;font-size:13px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#dc2626;">Urgency</p><p style="margin:8px 0 0 0;font-size:32px;font-weight:900;color:#991b1b;">Every Week</p><p style="margin:8px 0 0 0;font-size:14px;line-height:1.6;color:#475569;">Replace with your urgency message.</p></article>
    </div>
  </section>
  <section style="max-width:1100px;margin:0 auto;padding:36px 20px 56px;display:grid;grid-template-columns:1.2fr .8fr;gap:20px;">
    <article style="background:#fff;border:1px solid #e2e8f0;border-radius:16px;padding:24px;">
      <h2 style="margin:0;font-size:30px;font-weight:900;color:#1A5276;">Primary story section</h2>
      <p style="margin:14px 0 0 0;font-size:16px;line-height:1.75;color:#475569;">Use this area for your core narrative and why your work matters now.</p>
    </article>
    <aside style="background:linear-gradient(180deg,#f0f9ff 0%,#e0f2fe 100%);border:1px solid #bae6fd;border-radius:16px;padding:24px;">
      <h3 style="margin:0;font-size:22px;font-weight:900;color:#0c4a6e;">Primary CTA</h3>
      <p style="margin:12px 0 0 0;font-size:14px;line-height:1.7;color:#0f172a;">Replace with one clear action.</p>
      <a href="/contact-us" style="display:inline-block;margin-top:14px;background:#0ea5e9;color:#fff;font-weight:800;padding:10px 16px;border-radius:999px;text-decoration:none;">Take action</a>
    </aside>
  </section>
</main>
`.trim();

const LANDING_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#ffffff;color:#1C2833;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="background:#0f172a;color:#fff;">
    <div style="max-width:1000px;margin:0 auto;padding:64px 20px;text-align:center;">
      <p style="margin:0;font-size:12px;letter-spacing:.3em;text-transform:uppercase;font-weight:800;color:#67e8f9;">Landing</p>
      <h1 style="margin:14px 0 0 0;font-size:50px;line-height:1.08;font-weight:900;">Landing page headline focused on one clear conversion goal.</h1>
      <p style="margin:18px auto 0;max-width:760px;font-size:20px;line-height:1.7;color:rgba(255,255,255,0.88);">This layout is optimized for one offer, one audience, one primary conversion action.</p>
      <div style="margin-top:24px;display:flex;justify-content:center;gap:12px;flex-wrap:wrap;">
        <a href="#" style="display:inline-block;background:#22d3ee;color:#0f172a;font-weight:900;padding:12px 24px;border-radius:999px;text-decoration:none;">Primary CTA</a>
        <a href="#" style="display:inline-block;border:1px solid rgba(255,255,255,.35);color:#fff;font-weight:700;padding:12px 24px;border-radius:999px;text-decoration:none;">Secondary CTA</a>
      </div>
    </div>
  </section>
  <section style="max-width:1000px;margin:0 auto;padding:52px 20px;">
    <h2 style="margin:0;font-size:34px;font-weight:900;color:#0f172a;">Why this offer works</h2>
    <div style="margin-top:20px;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px;">
      <article style="padding:18px;border:1px solid #e2e8f0;border-radius:12px;"><h3 style="margin:0;font-size:18px;color:#0f172a;">Benefit 1</h3><p style="margin:10px 0 0 0;color:#475569;font-size:14px;line-height:1.65;">Replace with concrete value.</p></article>
      <article style="padding:18px;border:1px solid #e2e8f0;border-radius:12px;"><h3 style="margin:0;font-size:18px;color:#0f172a;">Benefit 2</h3><p style="margin:10px 0 0 0;color:#475569;font-size:14px;line-height:1.65;">Replace with differentiator.</p></article>
      <article style="padding:18px;border:1px solid #e2e8f0;border-radius:12px;"><h3 style="margin:0;font-size:18px;color:#0f172a;">Benefit 3</h3><p style="margin:10px 0 0 0;color:#475569;font-size:14px;line-height:1.65;">Replace with proof point.</p></article>
    </div>
  </section>
  <section style="background:#f8fafc;border-top:1px solid #e2e8f0;border-bottom:1px solid #e2e8f0;">
    <div style="max-width:1000px;margin:0 auto;padding:36px 20px;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;">
      <div style="text-align:center;"><p style="margin:0;font-size:28px;font-weight:900;color:#0f172a;">95%</p><p style="margin:8px 0 0 0;color:#475569;font-size:13px;">Result metric</p></div>
      <div style="text-align:center;"><p style="margin:0;font-size:28px;font-weight:900;color:#0f172a;">1,200+</p><p style="margin:8px 0 0 0;color:#475569;font-size:13px;">Users served</p></div>
      <div style="text-align:center;"><p style="margin:0;font-size:28px;font-weight:900;color:#0f172a;">24h</p><p style="margin:8px 0 0 0;color:#475569;font-size:13px;">Average response</p></div>
      <div style="text-align:center;"><p style="margin:0;font-size:28px;font-weight:900;color:#0f172a;">4.9/5</p><p style="margin:8px 0 0 0;color:#475569;font-size:13px;">Satisfaction</p></div>
    </div>
  </section>
</main>
`.trim();

const LEGAL_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#f8fafc;color:#0f172a;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="background:#ffffff;border-bottom:1px solid #e2e8f0;">
    <div style="max-width:900px;margin:0 auto;padding:44px 20px;">
      <p style="margin:0;font-size:12px;letter-spacing:.2em;text-transform:uppercase;font-weight:800;color:#334155;">Legal</p>
      <h1 style="margin:12px 0 0 0;font-size:40px;line-height:1.15;font-weight:900;">Policy title (Privacy Policy, Terms of Service, etc.)</h1>
      <p style="margin:14px 0 0 0;color:#475569;font-size:15px;line-height:1.7;">Use precise legal language and update each section with approved legal copy.</p>
      <div style="margin-top:16px;padding:12px 14px;border-radius:10px;background:#f1f5f9;border:1px solid #cbd5e1;font-size:13px;color:#334155;"><strong>Last updated:</strong> YYYY-MM-DD</div>
    </div>
  </section>
  <section style="max-width:900px;margin:0 auto;padding:28px 20px 56px;display:flex;flex-direction:column;gap:16px;">
    <article style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:20px;"><h2 style="margin:0;font-size:24px;font-weight:900;">1. Scope</h2><p style="margin:12px 0 0 0;color:#475569;line-height:1.75;">Define who this policy applies to and which services it covers.</p></article>
    <article style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:20px;"><h2 style="margin:0;font-size:24px;font-weight:900;">2. Data / Terms</h2><p style="margin:12px 0 0 0;color:#475569;line-height:1.75;">Replace this with official legal language reviewed by counsel.</p></article>
    <article style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:20px;"><h2 style="margin:0;font-size:24px;font-weight:900;">3. Contact and disputes</h2><p style="margin:12px 0 0 0;color:#475569;line-height:1.75;">Add your legal contact details and dispute process.</p></article>
  </section>
</main>
`.trim();

const GENERIC_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#ffffff;color:#1e293b;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="max-width:1024px;margin:0 auto;padding:56px 20px 26px;">
    <p style="margin:0;font-size:12px;letter-spacing:.26em;text-transform:uppercase;font-weight:800;color:#64748b;">Generic</p>
    <h1 style="margin:12px 0 0 0;font-size:44px;line-height:1.12;font-weight:900;color:#0f172a;">General content page headline</h1>
    <p style="margin:16px 0 0 0;font-size:18px;line-height:1.7;color:#475569;">Use this flexible layout when your page does not need a specialized template.</p>
  </section>
  <section style="max-width:1024px;margin:0 auto;padding:0 20px 24px;display:grid;grid-template-columns:2fr 1fr;gap:20px;">
    <article style="border:1px solid #e2e8f0;border-radius:14px;padding:22px;"><h2 style="margin:0;font-size:28px;color:#0f172a;">Main content section</h2><p style="margin:12px 0 0 0;line-height:1.75;color:#475569;">Replace this paragraph with your primary content.</p></article>
    <aside style="border:1px solid #e2e8f0;border-radius:14px;padding:22px;background:#f8fafc;"><h3 style="margin:0;font-size:20px;color:#0f172a;">Quick links</h3><ul style="margin:12px 0 0 18px;padding:0;line-height:1.8;color:#334155;"><li>Link one</li><li>Link two</li><li>Link three</li></ul></aside>
  </section>
  <section style="max-width:1024px;margin:0 auto;padding:0 20px 56px;">
    <div style="overflow:auto;border:1px solid #e2e8f0;border-radius:12px;">
      <table style="width:100%;border-collapse:collapse;font-size:14px;">
        <thead><tr style="background:#f8fafc;"><th style="text-align:left;padding:12px;border-bottom:1px solid #e2e8f0;">Column A</th><th style="text-align:left;padding:12px;border-bottom:1px solid #e2e8f0;">Column B</th></tr></thead>
        <tbody><tr><td style="padding:12px;border-bottom:1px solid #e2e8f0;">Sample value</td><td style="padding:12px;border-bottom:1px solid #e2e8f0;">Sample detail</td></tr><tr><td style="padding:12px;">Sample value</td><td style="padding:12px;">Sample detail</td></tr></tbody>
      </table>
    </div>
  </section>
</main>
`.trim();

const CRISIS_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#fff7ed;color:#111827;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="background:linear-gradient(180deg,#7f1d1d 0%,#991b1b 100%);color:#fff;">
    <div style="max-width:1100px;margin:0 auto;padding:56px 20px 52px;">
      <p style="margin:0;font-size:12px;letter-spacing:.35em;text-transform:uppercase;font-weight:900;color:#fde68a;">The Crisis</p>
      <h1 style="margin:14px 0 0 0;font-size:46px;line-height:1.1;font-weight:900;">Crisis headline that communicates urgency and real-world impact.</h1>
      <p style="margin:16px 0 0 0;font-size:20px;line-height:1.7;color:rgba(255,255,255,0.9);">Structure this page around urgency, evidence, and immediate action.</p>
    </div>
  </section>
  <section style="background:#dc2626;color:#fff;">
    <div style="max-width:1100px;margin:0 auto;padding:12px 20px;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;">
      <p style="margin:0;font-weight:700;font-size:14px;">Urgent alert: replace with your most important immediate-risk statement.</p>
      <span style="border:1px solid rgba(255,255,255,.5);border-radius:999px;padding:6px 10px;font-size:11px;letter-spacing:.08em;text-transform:uppercase;font-weight:800;">Emergency context</span>
    </div>
  </section>
  <section style="max-width:1100px;margin:0 auto;padding:36px 20px;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;">
    <article style="background:#fff;border:1px solid #fecaca;border-radius:12px;padding:16px;"><p style="margin:0;font-size:12px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#991b1b;">Stage 1</p><p style="margin:8px 0 0 0;color:#7f1d1d;line-height:1.7;">Initial interruption or risk trigger.</p></article>
    <article style="background:#fff;border:1px solid #fdba74;border-radius:12px;padding:16px;"><p style="margin:0;font-size:12px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#9a3412;">Stage 2</p><p style="margin:8px 0 0 0;color:#7c2d12;line-height:1.7;">Escalation and widening impact.</p></article>
    <article style="background:#fff;border:1px solid #fca5a5;border-radius:12px;padding:16px;"><p style="margin:0;font-size:12px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#991b1b;">Stage 3</p><p style="margin:8px 0 0 0;color:#7f1d1d;line-height:1.7;">Critical consequence without support.</p></article>
  </section>
</main>
`.trim();

const PROGRAM_OVERVIEW_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#f8fafc;color:#0f172a;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="background:#1e3a8a;color:#fff;">
    <div style="max-width:1100px;margin:0 auto;padding:56px 20px;">
      <p style="margin:0;font-size:12px;letter-spacing:.3em;text-transform:uppercase;font-weight:800;color:#bfdbfe;">Program</p>
      <h1 style="margin:14px 0 0 0;font-size:44px;line-height:1.12;font-weight:900;">Program headline that defines who this serves and why.</h1>
      <p style="margin:16px 0 0 0;font-size:19px;line-height:1.7;color:#dbeafe;">Use this template to describe outcomes, scope, and delivery model.</p>
    </div>
  </section>
  <section style="max-width:1100px;margin:0 auto;padding:36px 20px;">
    <h2 style="margin:0;font-size:30px;font-weight:900;color:#1e3a8a;">Program pillars</h2>
    <div style="margin-top:16px;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px;">
      <article style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:18px;"><h3 style="margin:0;font-size:20px;">Pillar 1</h3><p style="margin:10px 0 0 0;color:#475569;line-height:1.7;">Define intervention area.</p></article>
      <article style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:18px;"><h3 style="margin:0;font-size:20px;">Pillar 2</h3><p style="margin:10px 0 0 0;color:#475569;line-height:1.7;">Define delivery mechanism.</p></article>
      <article style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:18px;"><h3 style="margin:0;font-size:20px;">Pillar 3</h3><p style="margin:10px 0 0 0;color:#475569;line-height:1.7;">Define expected outcomes.</p></article>
    </div>
  </section>
  <section style="max-width:1100px;margin:0 auto;padding:0 20px 56px;">
    <ol style="margin:0;padding:0;list-style:none;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;">
      <li style="background:#fff;border:1px solid #cbd5e1;border-radius:12px;padding:14px;"><p style="margin:0;font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:#334155;font-weight:800;">Step 1</p><p style="margin:8px 0 0 0;color:#475569;">Intake</p></li>
      <li style="background:#fff;border:1px solid #cbd5e1;border-radius:12px;padding:14px;"><p style="margin:0;font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:#334155;font-weight:800;">Step 2</p><p style="margin:8px 0 0 0;color:#475569;">Verification</p></li>
      <li style="background:#fff;border:1px solid #cbd5e1;border-radius:12px;padding:14px;"><p style="margin:0;font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:#334155;font-weight:800;">Step 3</p><p style="margin:8px 0 0 0;color:#475569;">Execution</p></li>
      <li style="background:#fff;border:1px solid #cbd5e1;border-radius:12px;padding:14px;"><p style="margin:0;font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:#334155;font-weight:800;">Step 4</p><p style="margin:8px 0 0 0;color:#475569;">Reporting</p></li>
    </ol>
  </section>
</main>
`.trim();

const STORY_COMMUNITY_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#f9fafb;color:#111827;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="background:#0b4f6c;color:#fff;">
    <div style="max-width:1100px;margin:0 auto;padding:56px 20px 44px;">
      <p style="margin:0;font-size:12px;letter-spacing:.3em;text-transform:uppercase;font-weight:800;color:#fef08a;">Community</p>
      <h1 style="margin:14px 0 0 0;font-size:44px;line-height:1.12;font-weight:900;">Community-led headline centered on people and lived experience.</h1>
      <p style="margin:16px 0 0 0;font-size:19px;line-height:1.7;color:rgba(255,255,255,.9);">Use this for story-driven pages that build trust and participation.</p>
    </div>
  </section>
  <section style="max-width:1100px;margin:0 auto;padding:28px 20px;display:grid;grid-template-columns:1.2fr .8fr;gap:16px;">
    <article style="background:#fff;border:1px solid #dbeafe;border-radius:14px;padding:22px;">
      <h2 style="margin:0;font-size:30px;font-weight:900;color:#0b4f6c;">Featured story</h2>
      <p style="margin:12px 0 0 0;color:#475569;line-height:1.8;">Introduce one real person or family story and what changed.</p>
    </article>
    <aside style="background:#ecfeff;border:1px solid #a5f3fc;border-radius:14px;padding:22px;">
      <p style="margin:0;font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:#155e75;font-weight:800;">Quote</p>
      <p style="margin:10px 0 0 0;font-style:italic;line-height:1.7;color:#164e63;">Replace with a testimonial from your community.</p>
    </aside>
  </section>
  <section style="max-width:1100px;margin:0 auto;padding:0 20px 56px;">
    <h3 style="margin:0 0 14px 0;font-size:24px;font-weight:900;color:#0b4f6c;">Community highlights</h3>
    <div style="display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;">
      <article style="background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:14px;">Person / group 1</article>
      <article style="background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:14px;">Person / group 2</article>
      <article style="background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:14px;">Person / group 3</article>
      <article style="background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:14px;">Person / group 4</article>
    </div>
  </section>
</main>
`.trim();

const TRUST_LEGAL_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#f8fafc;color:#0f172a;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="background:#111827;color:#fff;">
    <div style="max-width:1000px;margin:0 auto;padding:56px 20px;">
      <p style="margin:0;font-size:12px;letter-spacing:.25em;text-transform:uppercase;font-weight:800;color:#93c5fd;">Trust & Legal</p>
      <h1 style="margin:14px 0 0 0;font-size:42px;line-height:1.15;font-weight:900;">Trust and compliance headline</h1>
      <p style="margin:16px 0 0 0;font-size:18px;line-height:1.7;color:#d1d5db;">Use this for transparency, standards, and accountability communication.</p>
    </div>
  </section>
  <section style="max-width:1000px;margin:0 auto;padding:34px 20px 18px;">
    <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;">
      <article style="background:#fff;border:1px solid #d1d5db;border-radius:12px;padding:16px;"><h3 style="margin:0;font-size:20px;">Commitment 1</h3><p style="margin:10px 0 0 0;color:#4b5563;line-height:1.7;">Define policy commitment.</p></article>
      <article style="background:#fff;border:1px solid #d1d5db;border-radius:12px;padding:16px;"><h3 style="margin:0;font-size:20px;">Commitment 2</h3><p style="margin:10px 0 0 0;color:#4b5563;line-height:1.7;">Define reporting standard.</p></article>
      <article style="background:#fff;border:1px solid #d1d5db;border-radius:12px;padding:16px;"><h3 style="margin:0;font-size:20px;">Commitment 3</h3><p style="margin:10px 0 0 0;color:#4b5563;line-height:1.7;">Define review cadence.</p></article>
    </div>
  </section>
  <section style="max-width:1000px;margin:0 auto;padding:0 20px 56px;">
    <article style="background:#fff;border:1px solid #d1d5db;border-radius:12px;padding:18px;">
      <h2 style="margin:0;font-size:26px;font-weight:900;">Compliance checklist</h2>
      <ul style="margin:12px 0 0 18px;padding:0;color:#374151;line-height:1.8;">
        <li>Item 1 - policy documentation</li>
        <li>Item 2 - legal review cycle</li>
        <li>Item 3 - public reporting commitment</li>
      </ul>
    </article>
  </section>
</main>
`.trim();

const PARTNERSHIP_CTA_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#ffffff;color:#111827;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="background:linear-gradient(180deg,#075985 0%,#0369a1 100%);color:#fff;">
    <div style="max-width:1060px;margin:0 auto;padding:58px 20px;">
      <p style="margin:0;font-size:12px;letter-spacing:.28em;text-transform:uppercase;font-weight:800;color:#fde68a;">Partnership</p>
      <h1 style="margin:14px 0 0 0;font-size:46px;line-height:1.12;font-weight:900;">Partnership headline that explains collaboration value clearly.</h1>
      <p style="margin:16px 0 0 0;font-size:19px;line-height:1.7;color:rgba(255,255,255,.9);">Use this for partnership opportunities and next-step contact flows.</p>
    </div>
  </section>
  <section style="max-width:1060px;margin:0 auto;padding:34px 20px;">
    <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;">
      <article style="border:1px solid #bae6fd;background:#f0f9ff;border-radius:12px;padding:18px;"><h3 style="margin:0;font-size:22px;color:#0c4a6e;">Partner type A</h3><p style="margin:10px 0 0 0;color:#475569;line-height:1.7;">Explain fit and expected contribution.</p></article>
      <article style="border:1px solid #bae6fd;background:#f0f9ff;border-radius:12px;padding:18px;"><h3 style="margin:0;font-size:22px;color:#0c4a6e;">Partner type B</h3><p style="margin:10px 0 0 0;color:#475569;line-height:1.7;">Explain fit and expected contribution.</p></article>
      <article style="border:1px solid #bae6fd;background:#f0f9ff;border-radius:12px;padding:18px;"><h3 style="margin:0;font-size:22px;color:#0c4a6e;">Partner type C</h3><p style="margin:10px 0 0 0;color:#475569;line-height:1.7;">Explain fit and expected contribution.</p></article>
    </div>
  </section>
  <section style="max-width:1060px;margin:0 auto;padding:0 20px 56px;">
    <div style="border-radius:14px;background:#0f172a;color:#fff;padding:28px;text-align:center;">
      <h2 style="margin:0;font-size:32px;font-weight:900;">Ready to partner?</h2>
      <p style="margin:12px auto 0;max-width:680px;color:#cbd5e1;line-height:1.75;">Replace this with your partner call-to-action and response timeframe.</p>
      <a href="/contact-us" style="display:inline-block;margin-top:18px;background:#22d3ee;color:#0f172a;font-weight:900;padding:12px 22px;border-radius:999px;text-decoration:none;">Start partnership discussion</a>
    </div>
  </section>
</main>
`.trim();

const ABOUT_MISSION_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#f9fafb;color:#0f172a;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="background:#0b3b5a;color:#fff;">
    <div style="max-width:1060px;margin:0 auto;padding:56px 20px;">
      <p style="margin:0;font-size:12px;letter-spacing:.3em;text-transform:uppercase;font-weight:800;color:#facc15;">About</p>
      <h1 style="margin:14px 0 0 0;font-size:46px;line-height:1.1;font-weight:900;">Mission headline that explains purpose and long-term direction.</h1>
      <p style="margin:16px 0 0 0;font-size:19px;line-height:1.7;color:rgba(255,255,255,.9);">Use this for about pages, mission statements, and organizational context.</p>
    </div>
  </section>
  <section style="max-width:1060px;margin:0 auto;padding:34px 20px;">
    <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;">
      <article style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:18px;"><h3 style="margin:0;font-size:22px;color:#0b3b5a;">Mission</h3><p style="margin:10px 0 0 0;color:#475569;line-height:1.7;">Describe what you do now.</p></article>
      <article style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:18px;"><h3 style="margin:0;font-size:22px;color:#0b3b5a;">Vision</h3><p style="margin:10px 0 0 0;color:#475569;line-height:1.7;">Describe long-term direction.</p></article>
      <article style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:18px;"><h3 style="margin:0;font-size:22px;color:#0b3b5a;">Values</h3><p style="margin:10px 0 0 0;color:#475569;line-height:1.7;">Describe guiding principles.</p></article>
    </div>
  </section>
  <section style="max-width:1060px;margin:0 auto;padding:0 20px 56px;">
    <article style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:22px;">
      <h2 style="margin:0;font-size:30px;font-weight:900;color:#0b3b5a;">Our story</h2>
      <p style="margin:12px 0 0 0;color:#475569;line-height:1.8;">Add your origin story, growth milestones, and where your work is headed.</p>
    </article>
  </section>
</main>
`.trim();

const VIDEO_GALLERY_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#020617;color:#e2e8f0;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="background:linear-gradient(180deg,#0f172a 0%,#1e293b 100%);border-bottom:1px solid rgba(148,163,184,.25);">
    <div style="max-width:1100px;margin:0 auto;padding:56px 20px;">
      <p style="margin:0;font-size:12px;letter-spacing:.3em;text-transform:uppercase;font-weight:800;color:#67e8f9;">Video Gallery</p>
      <h1 style="margin:14px 0 0 0;font-size:44px;line-height:1.12;font-weight:900;color:#f8fafc;">Video stories and educational content in one place.</h1>
      <p style="margin:16px 0 0 0;font-size:18px;line-height:1.75;color:#cbd5e1;">Replace these placeholders with featured videos, playlists, and context captions.</p>
    </div>
  </section>
  <section style="max-width:1100px;margin:0 auto;padding:30px 20px 16px;">
    <article style="border:1px solid #334155;border-radius:14px;overflow:hidden;background:#0b1220;">
      <div style="aspect-ratio:16/9;background:#111827;display:flex;align-items:center;justify-content:center;font-size:14px;color:#94a3b8;">Embed featured video iframe here</div>
      <div style="padding:16px 18px;">
        <h2 style="margin:0;font-size:26px;font-weight:900;color:#f8fafc;">Featured Video Title</h2>
        <p style="margin:10px 0 0 0;color:#cbd5e1;line-height:1.75;">Short context for the featured video and why it matters.</p>
      </div>
    </article>
  </section>
  <section style="max-width:1100px;margin:0 auto;padding:0 20px 56px;">
    <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;">
      <article style="background:#0f172a;border:1px solid #334155;border-radius:12px;overflow:hidden;"><div style="aspect-ratio:16/9;background:#1f2937;display:flex;align-items:center;justify-content:center;color:#64748b;font-size:12px;">Video 1</div><div style="padding:12px;color:#e2e8f0;font-size:14px;">Video title or playlist item</div></article>
      <article style="background:#0f172a;border:1px solid #334155;border-radius:12px;overflow:hidden;"><div style="aspect-ratio:16/9;background:#1f2937;display:flex;align-items:center;justify-content:center;color:#64748b;font-size:12px;">Video 2</div><div style="padding:12px;color:#e2e8f0;font-size:14px;">Video title or playlist item</div></article>
      <article style="background:#0f172a;border:1px solid #334155;border-radius:12px;overflow:hidden;"><div style="aspect-ratio:16/9;background:#1f2937;display:flex;align-items:center;justify-content:center;color:#64748b;font-size:12px;">Video 3</div><div style="padding:12px;color:#e2e8f0;font-size:14px;">Video title or playlist item</div></article>
    </div>
  </section>
</main>
`.trim();

const PHOTO_GALLERY_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#ffffff;color:#0f172a;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="max-width:1120px;margin:0 auto;padding:52px 20px 22px;">
    <p style="margin:0;font-size:12px;letter-spacing:.28em;text-transform:uppercase;font-weight:800;color:#64748b;">Photo Gallery</p>
    <h1 style="margin:12px 0 0 0;font-size:44px;line-height:1.12;font-weight:900;">Moments from the community and mission in action.</h1>
    <p style="margin:14px 0 0 0;font-size:18px;line-height:1.75;color:#475569;">Use this template for image-based storytelling with captions and context.</p>
  </section>
  <section style="max-width:1120px;margin:0 auto;padding:0 20px 56px;">
    <div style="display:grid;grid-template-columns:2fr 1fr 1fr;grid-auto-rows:170px;gap:12px;">
      <figure style="margin:0;grid-row:span 2;border-radius:12px;overflow:hidden;border:1px solid #e2e8f0;background:#f1f5f9;display:flex;align-items:center;justify-content:center;color:#64748b;font-size:13px;">Featured photo</figure>
      <figure style="margin:0;border-radius:12px;overflow:hidden;border:1px solid #e2e8f0;background:#f8fafc;display:flex;align-items:center;justify-content:center;color:#64748b;font-size:12px;">Photo 2</figure>
      <figure style="margin:0;border-radius:12px;overflow:hidden;border:1px solid #e2e8f0;background:#f8fafc;display:flex;align-items:center;justify-content:center;color:#64748b;font-size:12px;">Photo 3</figure>
      <figure style="margin:0;border-radius:12px;overflow:hidden;border:1px solid #e2e8f0;background:#f8fafc;display:flex;align-items:center;justify-content:center;color:#64748b;font-size:12px;">Photo 4</figure>
      <figure style="margin:0;border-radius:12px;overflow:hidden;border:1px solid #e2e8f0;background:#f8fafc;display:flex;align-items:center;justify-content:center;color:#64748b;font-size:12px;">Photo 5</figure>
    </div>
    <article style="margin-top:18px;border:1px solid #e2e8f0;border-radius:12px;padding:16px;background:#ffffff;">
      <h2 style="margin:0;font-size:24px;font-weight:900;">Gallery notes and context</h2>
      <p style="margin:10px 0 0 0;color:#475569;line-height:1.75;">Add event details, date, location, and what each gallery set represents.</p>
    </article>
  </section>
</main>
`.trim();

const PATIENT_STORIES_LAYOUT_TEMPLATE = `
<main style="min-height:100vh;background:#f8fafc;color:#111827;font-family:Inter,Segoe UI,Arial,sans-serif;">
  <section style="background:linear-gradient(180deg,#0b4f6c 0%,#155e75 100%);color:#fff;">
    <div style="max-width:1040px;margin:0 auto;padding:56px 20px;">
      <p style="margin:0;font-size:12px;letter-spacing:.3em;text-transform:uppercase;font-weight:800;color:#fef08a;">Patient Stories</p>
      <h1 style="margin:14px 0 0 0;font-size:44px;line-height:1.12;font-weight:900;">Real journeys from patients and families.</h1>
      <p style="margin:16px 0 0 0;font-size:18px;line-height:1.75;color:rgba(255,255,255,.9);">Use this page to share lived experiences, milestones, and outcomes.</p>
    </div>
  </section>
  <section style="max-width:1040px;margin:0 auto;padding:28px 20px 56px;display:flex;flex-direction:column;gap:16px;">
    <article style="background:#fff;border:1px solid #dbeafe;border-radius:14px;padding:18px 20px;">
      <p style="margin:0;font-size:12px;letter-spacing:.08em;text-transform:uppercase;font-weight:800;color:#0369a1;">Story 1</p>
      <h2 style="margin:10px 0 0 0;font-size:28px;font-weight:900;color:#0f172a;">Patient name or story title</h2>
      <p style="margin:12px 0 0 0;color:#475569;line-height:1.8;">Add the background, challenge, intervention, and progress for this patient story.</p>
    </article>
    <article style="background:#fff;border:1px solid #dbeafe;border-radius:14px;padding:18px 20px;">
      <p style="margin:0;font-size:12px;letter-spacing:.08em;text-transform:uppercase;font-weight:800;color:#0369a1;">Story 2</p>
      <h2 style="margin:10px 0 0 0;font-size:28px;font-weight:900;color:#0f172a;">Patient name or story title</h2>
      <p style="margin:12px 0 0 0;color:#475569;line-height:1.8;">Use consistent structure across stories to make them easier to read and compare.</p>
    </article>
    <aside style="border:1px solid #a5f3fc;background:#ecfeff;border-radius:12px;padding:16px;">
      <h3 style="margin:0;font-size:20px;font-weight:900;color:#155e75;">Story ethics checklist</h3>
      <ul style="margin:10px 0 0 18px;padding:0;color:#0f172a;line-height:1.75;">
        <li>Consent confirmed</li>
        <li>Sensitive details reviewed</li>
        <li>Outcome claims are evidence-based</li>
      </ul>
    </aside>
  </section>
</main>
`.trim();

export const CMS_EDITOR_BLOCKS: CMSEditorBlock[] = [
	{
		id: "blank-section",
		title: "Blank section",
		description: "Empty section scaffold to add a new content block.",
		html: BLANK_SECTION,
	},
	{
		id: "three-column-cards",
		title: "Three feature cards",
		description: "Three-column row with title, subtitle, and body text per card.",
		html: THREE_COLUMN_CARDS,
	},
	{
		id: "two-column-split",
		title: "Two column split",
		description: "Section label, headline, and a highlighted side column.",
		html: TWO_COLUMN_SPLIT,
	},
	{
		id: "cta-band",
		title: "CTA band",
		description: "Full-width gradient call-to-action with button.",
		html: CTA_BAND,
	},
	{
		id: "quote-block",
		title: "Quote block",
		description: "Pull quote with attribution.",
		html: QUOTE_BLOCK,
	},
];

export const CMS_PAGE_LAYOUT_TEMPLATES: readonly CMSPageTemplateOption[] = [
	{
		id: "home",
		label: "Home",
		description: "Homepage hero + section starter layout.",
		defaultType: "home",
		defaultShowTitle: false,
		html: HOME_LAYOUT_TEMPLATE,
	},
	{
		id: "landing",
		label: "Landing",
		description: "Campaign/offer style landing page starter.",
		defaultType: "landing",
		defaultShowTitle: false,
		html: LANDING_LAYOUT_TEMPLATE,
	},
	{
		id: "legal",
		label: "Legal",
		description: "Policy/terms style legal page starter.",
		defaultType: "legal",
		defaultShowTitle: true,
		html: LEGAL_LAYOUT_TEMPLATE,
	},
	{
		id: "generic",
		label: "Generic",
		description: "General purpose content page starter.",
		defaultType: "generic",
		defaultShowTitle: true,
		html: GENERIC_LAYOUT_TEMPLATE,
	},
	{
		id: "crisis",
		label: "Crisis",
		description: "Urgency-focused story and response framing.",
		defaultType: "generic",
		defaultShowTitle: false,
		html: CRISIS_LAYOUT_TEMPLATE,
	},
	{
		id: "program-overview",
		label: "Program Overview",
		description: "Program goals, scope, and outcomes structure.",
		defaultType: "generic",
		defaultShowTitle: false,
		html: PROGRAM_OVERVIEW_LAYOUT_TEMPLATE,
	},
	{
		id: "story-community",
		label: "Story + Community",
		description: "Community stories and engagement framework.",
		defaultType: "generic",
		defaultShowTitle: false,
		html: STORY_COMMUNITY_LAYOUT_TEMPLATE,
	},
	{
		id: "trust-legal",
		label: "Trust + Legal",
		description: "Trust, governance, and policy communication.",
		defaultType: "legal",
		defaultShowTitle: true,
		html: TRUST_LEGAL_LAYOUT_TEMPLATE,
	},
	{
		id: "partnership-cta",
		label: "Partnership CTA",
		description: "Partner-facing page with clear call-to-action.",
		defaultType: "generic",
		defaultShowTitle: false,
		html: PARTNERSHIP_CTA_LAYOUT_TEMPLATE,
	},
	{
		id: "about-mission",
		label: "About Mission",
		description: "About/mission narrative layout starter.",
		defaultType: "generic",
		defaultShowTitle: false,
		html: ABOUT_MISSION_LAYOUT_TEMPLATE,
	},
	{
		id: "video-gallery",
		label: "Video Gallery",
		description: "Featured video with supporting gallery grid.",
		defaultType: "generic",
		defaultShowTitle: false,
		html: VIDEO_GALLERY_LAYOUT_TEMPLATE,
	},
	{
		id: "photo-gallery",
		label: "Photo Gallery",
		description: "Image-focused gallery layout with captions section.",
		defaultType: "generic",
		defaultShowTitle: false,
		html: PHOTO_GALLERY_LAYOUT_TEMPLATE,
	},
	{
		id: "patient-stories",
		label: "Patient Stories",
		description: "Narrative story cards for patient and family journeys.",
		defaultType: "generic",
		defaultShowTitle: false,
		html: PATIENT_STORIES_LAYOUT_TEMPLATE,
	},
];

export function getCMSPageLayoutTemplate(
	templateId: CMSPageLayoutTemplate,
): CMSPageTemplateOption | undefined {
	return CMS_PAGE_LAYOUT_TEMPLATES.find((template) => template.id === templateId);
}
